"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = bootstrap;
var _propget = _interopRequireDefault(require("propget"));
/**
 * NOTE: This is an intentional global listener array so that when multiple instances
 * of an async API get initialized, the data modification is still broadcasted to
 * every single instance. Not the one that just making the changes. This is intentional.
 *
 * @type {Array}
 * @private
 */
let listeners = [];

/**
 * Create the Async API
 *
 * @param {Object} config Configuration object.
 * @param {String} config.namespace The global name we introduce ourselves under.
 * @param {Mixed} config.context The context or global we introduce the API under.
 * @returns {Object} API methods.
 * @public
 */
function bootstrap() {
  let {
    namespace = 'gas',
    context = global
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  /**
   * Add a new subscriber to state changes.
   *
   * @param {Function} listener Listener to subscribe to changes.
   * @returns {Function} Removes the assigned listener.
   * @public
   */
  function subscribe(listener) {
    listeners = [...listeners, listener];
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }

  /**
   * Emit when external state was modified.
   *
   * @public
   */
  function changed() {
    for (const listener of listeners) {
      listener();
    }
  }

  /**
   * Process the process the API calls by forwarding them to the existing
   * sync API's that should be ready once we the bootstrap is initialized.
   *
   * @param {Array} forward Arguments to forward.
   * @param {Function} fn Completion callback.
   * @returns {Number} Amount of items queued.
   * @public
   */
  function push(forward, fn) {
    let result;
    let target;
    let parent;
    try {
      const [name, ...args] = forward;
      switch (name) {
        //
        // Non existing API, this is new, and used to as part of of setup
        // sequence so you know that the async API has been initialized
        // and that you can start making changes. It's basically another
        // `ready` function.
        //
        case 'bootstrap':
          args[0]();
          break;
        case 'eldorado.data.merge':
        case 'data.merge':
          result = context.ux.data.merge(...args);
          changed();
          break;
        case 'eldorado.data.set':
        case 'data.set':
          result = context.ux.data.set(...args);
          changed();
          break;

        //
        // Any API that doesn't require any special case or alternation
        // will be proxed through `propget` to drill down into the `ux`
        // object and execute that given function.
        //
        default:
          target = (0, _propget.default)(context.ux, name);

          //
          // When we try to access a deeply nested object's function we want to
          // make sure that we correctly assign the `this` value of that
          // function. Given that dot notation is used to drill down deeply we
          // can easily find the parent object and use that value to correctly
          // assign the `thisArg` for the function. This ensures that things
          // like EventEmitters still have access to the right `_events`
          // object to assign listeners etc.
          //
          parent = name.includes('.') ? (0, _propget.default)(context.ux, name.split('.').slice(0, -1).join('.')) : context.ux;
          result = typeof target === 'function' ? target.call(parent, ...args) : target;
      }
    } catch (e) {
      return fn && fn(e);
    }
    if (fn) fn(void 0, result);
    return 0;
  }

  /**
   * Flush the current queued up set of events to the push function.
   *
   * @public
   */
  function flush() {
    const queue = Array.isArray(context[namespace]) ? context[namespace].slice(0) : [];

    //
    // Clean up the previous queued commands to prevent us from retaining
    // references to the code. We already made a clone of the items, so we
    // override and introduce the API if it does not exist.
    //
    if (Array.isArray(context[namespace])) context[namespace].length = 0;
    if (!context[namespace] || context[namespace].push !== push) context[namespace] = {
      push
    };
    queue.forEach(function process(todo, index) {
      if (!Array.isArray(todo)) return; // Skipping callback functions.
      const fn = queue[index + 1]; // Check for callbacks functions.

      push(todo, typeof fn === 'function' && fn);
    });
  }

  //
  // Order of operations is important here, we want to make sure we flush
  // the current queued commands to our new `push` method before we introduce
  // as the new main API that people would consume going forward.
  //
  flush();

  //
  // Expose all API methods so they can be consumed.
  //
  return {
    push,
    flush,
    subscribe,
    changed
  };
}